// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import { Pagination, Row, Col } from 'react-bootstrap';
import { sortByName } from '../helpers';
import type { MapStateToProps } from 'react-redux';

type Props = {
  showArchived?: boolean,
  currentOrganisation: Object,
  context?: any,
  items: any[],
  itemsPerPage?: number,
  keys?: string[],
  filterValue: ?string,
  component: any,
  fuzzyFilterFactory: any,
  pluralName?: string,
  pageButtonLimit?: number,
  threshold?: number,
  sortOrder?: string,
  distance?: number
};

type State = {
  totalItems: number,
  currentPage: number,
  visibleItems: any[],
  allItems: any[]
};

class PaginatedList extends React.Component<Props, State> {
  defaultItemsPerPage: number;
  defaultKeys: string[];

  constructor(props) {
    super(props);
    this.state = {
      totalItems: 0,
      currentPage: 1,
      visibleItems: [],
      allItems: []
    };

    this.defaultItemsPerPage = 10;
    this.defaultKeys = ['id', 'name'];
  }

  componentDidMount() {
    this.handleAllItems(this.props);
  }

  componentWillReceiveProps(nextProps: Props) {
    const oldShowArchived = this.props.showArchived;
    const newShowArchived = nextProps.showArchived;
    if (oldShowArchived !== newShowArchived) {
      this.handleAllItems(nextProps);
    }

    // current org has changed (need for NDM changes)
    const oldCurrentOrgId = this.props.currentOrganisation.id;
    const newCurrentOrgId = nextProps.currentOrganisation.id;
    if (oldCurrentOrgId !== newCurrentOrgId) {
      this.handleAllItems(nextProps);
    }

    // context has changed
    const oldContext = this.props.context;
    const newContext = nextProps.context;
    if (oldContext !== newContext) {
      this.handleAllItems(nextProps);
    }

    // itemsListChanged
    const oldItems = JSON.stringify(this.props.items);
    const newItems = JSON.stringify(nextProps.items);
    if (oldItems !== newItems) {
      this.handleAllItems(nextProps);
    }
  }

  setVisibleItems = (allItems = [], props: Props) => {
    const currentPage = this.state.currentPage;
    const visibleItems = this.calcVisibleItems(allItems, props, currentPage);

    if (allItems.length && !visibleItems.length) {
      // no visible items on this page, try the previous page.
      this.changePage(currentPage - 1);
    }

    this.setState({ visibleItems });
  };

  calcVisibleItems = (allItems, props: Props, currentPage: number) => {
    const itemsPerPage = props.itemsPerPage || this.defaultItemsPerPage;
    const startIndex = currentPage * itemsPerPage - itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return allItems.slice(startIndex, endIndex);
  };

  handleAllItems = (props: Props) => {
    // filter out archived (if necessary)
    const { showArchived, items, keys } = props;
    const key = keys ? keys[1] : this.defaultKeys[1];
    const filteredItems = sortByName(items, key, props.sortOrder).filter(item =>
      showArchived ? item : !item.isArchived
    );

    // set appropriate state
    this.setState({
      allItems: filteredItems,
      totalItems: filteredItems.length
    });

    // set visible items
    this.setVisibleItems(filteredItems, props);
  };

  changePage = (newPageNumber: number) => {
    this.setState({ currentPage: newPageNumber }, () =>
      this.handleAllItems(this.props)
    );
  };

  renderList = () => {
    const {
      filterValue,
      component,
      fuzzyFilterFactory,
      pluralName = 'items',
      keys = this.defaultKeys,
      threshold = 0.1, // 0.0 is perfect match. 1.0 matches anything.
      distance = 1000 // distance * threshold = number of characters away from the start of the string the match can be found.
    } = this.props;

    const items =
      filterValue === '' ? this.state.visibleItems : this.state.allItems;
    const Component = component;

    if (fuzzyFilterFactory) {
      const { FilterResults } = fuzzyFilterFactory;
      return (
        <FilterResults items={items} fuseConfig={{ keys, threshold, distance }}>
          {filteredItems => {
            if (filteredItems.length) {
              return (
                <Col xs={12}>
                  {filteredItems.map(item => (
                    <Component key={item[keys[0]]} item={item} />
                  ))}
                </Col>
              );
            } else {
              return (
                <Col xs={12}>
                  <div className="well well-sm">No {pluralName} found</div>
                </Col>
              );
            }
          }}
        </FilterResults>
      );
    } else {
      return (
        <div>
          {items.length ? (
            <Col xs={12}>
              {items.map(item => (
                <Component key={item[keys[0]]} item={item} />
              ))}
            </Col>
          ) : (
            <Col xs={12}>
              <div className="well well-sm">No {pluralName} found</div>
            </Col>
          )}
        </div>
      );
    }
  };

  render() {
    const {
      filterValue,
      pageButtonLimit = 5,
      itemsPerPage = this.defaultItemsPerPage
    } = this.props;

    const numberOfPages = Math.ceil(this.state.totalItems / itemsPerPage);

    return (
      <Row>
        {this.renderList()}

        {filterValue === '' && this.state.totalItems > itemsPerPage && (
          <div className="text-center">
            <Pagination
              prev
              next
              ellipsis={numberOfPages > pageButtonLimit}
              items={numberOfPages}
              maxButtons={pageButtonLimit}
              activePage={this.state.currentPage}
              onSelect={this.changePage}
            />
          </div>
        )}
      </Row>
    );
  }
}

const mapStateToProps: MapStateToProps<*, *, *> = state => {
  return { currentOrganisation: state.currentOrganisation };
};

export default connect(mapStateToProps)(PaginatedList);



// WEBPACK FOOTER //
// ./src/components/PaginatedList.js